import {
  AmplitudeToFirebaseEvents,
  CreateOrderEventData,
  EventName,
  EventProperties as AmplitudeEventProps,
  AmplitudetoFacebookEvents,
  FacebookEventNames,
  EventPropertiesMap,
} from 'src/context/AmplitudeProvider/AmplitudeProvider.types';

export class AnalyticsEventMapper {
  /**
   * Converts parameters from a specific event to Firebase format.
   * @returns An object containing the Firebase event name and its parameters.
   */
  public static mapToFirebaseEvent<AmplitudeEventName extends EventName>(
    eventName: AmplitudeEventName,
    eventData: AmplitudeEventProps<AmplitudeEventName>
  ): {
    firebaseEventName: string | null;
    firebaseParams: Record<string, unknown>;
  } {
    const firebaseEventName = AmplitudeToFirebaseEvents[eventName] || null;

    if (!firebaseEventName) {
      return { firebaseEventName: null, firebaseParams: {} };
    }

    const firebaseParams = this.mapParams(eventName, eventData);

    return { firebaseEventName, firebaseParams };
  }

  /**
   * Converts parameters from a specific event to Facebook (App Events) format.
   * This is used by `capacitor-facebook-events` to log standard events.
   * @returns An object containing the Facebook event name and its parameters.
   */
  public static mapToFacebookEvent<AmplitudeEventName extends EventName>(
    eventName: AmplitudeEventName,
    eventData: AmplitudeEventProps<AmplitudeEventName>
  ): {
    facebookEventName: FacebookEventNames | null;
    facebookParams: Record<string, unknown>;
  } {
    const facebookEventName = AmplitudetoFacebookEvents[eventName];

    if (!facebookEventName) {
      return { facebookEventName: null, facebookParams: {} };
    }

    const facebookParams = this.getFacebookParams(eventName, eventData);

    return { facebookEventName, facebookParams };
  }

  /**
   * Internal mapper to Facebook events and params.
   */
  private static getFacebookParams<T extends EventName>(
    eventName: T,
    eventData: AmplitudeEventProps<T>
  ): Record<string, unknown> {
    switch (eventName) {
      case 'add_product_to_cart': {
        const data = eventData as EventPropertiesMap['add_product_to_cart'];
        const unitPrice = (data.discount_price ?? data.price) || 0;
        return {
          fb_content_type: 'product',
          fb_content_id: String(data.product_pk),
          value: unitPrice * (data.quantity || 1),
          currency: 'MXN',
        };
      }

      case 'sign_up': {
        return {
          fb_registration_method: 'app',
          value: 1,
        };
      }

      case 'order_created': {
        const data = eventData as EventPropertiesMap['order_created'];
        return {
          value: data.total_with_coupon || data.total,
          currency: 'MXN',
          transaction_id: data.payment_data_id ?? undefined,
          content_ids: (data.products || []).map((p) => String(p.product_id)),
        };
      }

      case 'product_search': {
        const data = eventData as EventPropertiesMap['product_search'];
        return {
          search_string: data.search,
          success: (data.results_count ?? 0) > 0,
        };
      }

      case 'categories_slider_click': {
        const data = eventData as EventPropertiesMap['categories_slider_click'];
        return {
          fb_content_type: 'category',
          fb_content_id: data.category_slug,
        };
      }
      case 'homepage_category_filter': {
        const data =
          eventData as EventPropertiesMap['homepage_category_filter'];
        return {
          fb_content_type: 'category',
          fb_content_id: data.category_slug,
        };
      }
      case 'product_detail_click': {
        const data = eventData as EventPropertiesMap['product_detail_click'];
        return {
          fb_content_type: 'product',
          fb_content_id: String(data.product_id),
        };
      }
      case 'home_section_see_more_click': {
        const data =
          eventData as EventPropertiesMap['home_section_see_more_click'];
        return {
          fb_content_type: 'home_section',
          fb_content_id: data.title,
        };
      }

      default:
        return { facebookEventName: null, facebookParams: {} };
    }
  }

  /**
   * Maps the parameters for a specific event.
   */
  private static mapParams(eventName: string, eventData: unknown) {
    switch (eventName) {
      case 'order_created':
        return this.mapOrderCreatedToPurchase(
          eventData as CreateOrderEventData
        );
      default:
        console.warn(`No parameter mapping defined for event: ${eventName}`);
        return {};
    }
  }

  /**
   * Maps `order_created` event data to Firebase `purchase` parameters.
   * @param eventData Data from the `order_created` event.
   * @returns Mapped parameters for Firebase Analytics.
   */
  private static mapOrderCreatedToPurchase(eventData: CreateOrderEventData) {
    return {
      transaction_id: eventData.payment_data_id ?? 'unknown',
      value: eventData.total_with_coupon || eventData.total,
      currency: 'MXN',
      tax: 0,
      shipping: eventData.delivery_price,
      coupon: eventData.coupon_code ?? undefined,
      items: eventData.products.map((product) => ({
        item_id: product.product_id,
      })),
    };
  }
}
